/*
 * Copyright © 2016-2020 The Thingsboard Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/* eslint-disable import/no-unresolved, import/default, import/namespace */

import pinGreenSvg from '../svg/pin_green.svg';
import pinYellowSvg from '../svg/pin_yellow.svg';
import pinGreySvg from '../svg/pin_grey.svg';
import pinRedSvg from '../svg/pin_red.svg';

import pinRedNoShadowSvg from '../svg/pin_red_noshadow.svg';
import pinGreenNoShadowSvg from '../svg/pin_green_noshadow.svg';

import siteGreenSvg from '../svg/site_green.svg';
import siteYellowSvg from '../svg/site_yellow.svg';
import siteGreySvg from '../svg/site_grey.svg';
import siteRedSvg from '../svg/site_red.svg';

import siloGreenSvg from '../svg/silo_green.svg';
import siloYellowSvg from '../svg/silo_yellow.svg';
import siloGreySvg from '../svg/silo_grey.svg';
import siloRedSvg from '../svg/silo_red.svg';

import siloGroupGreenSvg from '../svg/silo_group_green.svg';
import siloGroupYellowSvg from '../svg/silo_group_yellow.svg';
import siloGroupGreySvg from '../svg/silo_group_grey.svg';
import siloGroupRedSvg from '../svg/silo_group_red.svg';

import aerationGreenSvg from '../svg/aeration_green.svg';
import aerationYellowSvg from '../svg/aeration_yellow.svg';
import aerationGreySvg from '../svg/aeration_grey.svg';
import aerationRedSvg from '../svg/aeration_red.svg';

import aerationAlarmGreenSvg from '../svg/aeration_alarm_green.svg';
import aerationAlarmRedSvg from '../svg/aeration_alarm_red.svg';
import aerationAlarmGreySvg from '../svg/aeration_alarm_grey.svg';

import weatherStationGreenSvg from '../svg/weather_station_green.svg';
import weatherStationYellowSvg from '../svg/weather_station_yellow.svg';
import weatherStationGreySvg from '../svg/weather_station_grey.svg';
import weatherStationRedSvg from '../svg/weather_station_red.svg';

import aerationRoofGreenSvg from '../svg/aeration_roof_green.svg';
import aerationRoofRedSvg from '../svg/aeration_roof_red.svg';
import aerationRoofGreySvg from '../svg/aeration_roof_grey.svg';

import aerationRoofNoFanGreenSvg from '../svg/aeration_roof_nofan_green.svg';
import aerationRoofNoFanRedSvg from '../svg/aeration_roof_nofan_red.svg';
import aerationRoofNoFanGreySvg from '../svg/aeration_roof_nofan_grey.svg';

import aerationFanGreenSvg from '../svg/aeration_fan_green.svg';
import aerationFanRedSvg from '../svg/aeration_fan_red.svg';
import aerationFanGreySvg from '../svg/aeration_fan_grey.svg';


import relayRedSvg from '../svg/relay_red.svg';

import HDRTexture from '../textures/venice_sunset_1k.hdr'


/* eslint-enable import/no-unresolved, import/default, import/namespace */

const colors = {
    minColor: '#3498db', //'#1abc9c', // turquoise,
    avgColor: '#514f9d', // amethyst,
    maxColor: '#ed6d05', // pumpkin,
    errorColor: '#d53d0e',
    minErrorColor: '#007AA3',
    normalColor: '#27ae60',
    normalGreyColor: '#384044',
    tempMatrixColors: ['#2980b9', '#3498db', '#2ecc71', '#f1c40f', '#d53d0e'],
    moistureMatrixColors: ['#f1c40f', '#87b93a', '#27ae60', "#28988a", '#2980b9'],
    co2MatrixColors: ['#27ae60', '#87b93a', '#f1c40f', "#28988a", '#bf24b9'],
    errorValueColor: '#c0392b',
    hasAlarmsColor: '#ffb72d',
    mainGrey: '#848A90',
    disableColor: '#777777'
}

export default angular.module('agrolog.types', [])
    .constant('agTypes',
        {
            drawing3d: {
                colors: colors,


                showHeadup: false,
                showGrid: false,
                showCrop: false,
                showCables: false,
                showGround: false,
                showSelection: false,
                showCompass: false,
                showVolume: false,
                
                siloBackface: true,
                siloOpacity: 0.6,

                laserDeviceTypes: ['niveaubob4000', 'HR80G'],

                crop: {
                    clamp: false,
                    showWireframe: false,
                    showControlPoints: false,
                    elipson: 10,
                    method: 'gaussian'
                },

                selection: {
                    color: '#00B5D8',
                    width: 2.5
                },
                general: {
                    siloEmissive: '#dedede',
                    siloAlarmEmissive: colors.errorColor,
                    cropColor: colors.maxColor,
                    cropEmissive: colors.maxColor
                },
                material: {},
                
                sensor: {
                    radius: 1,
                    disableColor: '#777777',
                    disableOpacity: 1.0,
                },

                environment: {
                    skyColor: '#8da8b9', 
                    groundColor: '#9c7c41',
                    intensity: 1,
                    shadowOpacity: 0.1,
                    sunColor: "#ffffff",
                    sunIntensity: 0.5,
                    exposure: 0.7,
                    hdrTexture: HDRTexture
                },
            
                icons: {
                    pin: {
                        normal: pinGreenNoShadowSvg,
                        alarm: pinRedNoShadowSvg,
                        stale: pinGreySvg
                    },
                    typeIcons: {
                        silo: {
                            alarm: siloRedSvg,
                            stale: siloGreySvg,
                            normal: siloGreenSvg
                        },
                        silo_group: {
                            alarm: siloGroupRedSvg,
                            stale: siloGroupGreySvg,
                            normal: siloGroupGreenSvg
                        },
                        weather_station: {
                            alarm: weatherStationRedSvg,
                            stale: weatherStationGreySvg,
                            normal: weatherStationGreenSvg
                        },
                        site: {
                            alarm: siteRedSvg,
                            stale: siteGreySvg,
                            normal: siteGreenSvg
                        },
                        aeration: {
                            alarm: aerationRedSvg,
                            stale: aerationGreySvg,
                            normal: aerationGreenSvg
                        }
                    },
                    fanIcons: {
                        normal: aerationFanGreenSvg,
                        alarm: aerationFanRedSvg,
                        stale: aerationFanGreySvg
                    },
                    aerationIcons: {
                        aeration: {
                            normal: aerationGreenSvg,
                            alarm: aerationRedSvg,
                            stale: aerationGreySvg
                        },
                        alarm: {
                            normal: aerationAlarmGreenSvg,
                            alarm: aerationAlarmRedSvg,
                            stale: aerationAlarmGreySvg
                        },
                        roof: {
                            normal: aerationRoofNoFanGreenSvg,
                            alarm: aerationRoofNoFanRedSvg,
                            stale: aerationRoofNoFanGreySvg
                        }
                    }
                }
            },
            assetType: {
                site: 'site',
                siloGroup: 'silo_group',
                silo: 'silo',
                aeration: 'aeration',
                weatherStation: 'weather_station',
                tenantInfo: 'tenant-info'
            },
            colors: colors,
            icons: {
                pin: {
                    highest: pinRedSvg,
                    hasAlarms: pinYellowSvg,
                    hasAckAlarm: pinGreySvg,
                    noAlarms: pinGreenSvg
                },
                site: {
                    highest: siteRedSvg,
                    hasAlarms: siteYellowSvg,
                    hasAckAlarm: siteGreySvg,
                    noAlarms: siteGreenSvg
                },
                silo: {
                    highest: siloRedSvg,
                    hasAlarms: siloYellowSvg,
                    hasAckAlarm: siloGreySvg,
                    noAlarms: siloGreenSvg
                },
                silo_group: {
                    highest: siloGroupRedSvg,
                    hasAlarms: siloGroupYellowSvg,
                    hasAckAlarm: siloGroupGreySvg,
                    noAlarms: siloGroupGreenSvg
                },
                aeration: {
                    highest: aerationRedSvg,
                    hasAlarms: aerationYellowSvg,
                    hasAckAlarm: aerationGreySvg,
                    noAlarms: aerationGreenSvg
                },
                alarm: {
                    highest: aerationAlarmRedSvg,
                    hasAckAlarm: aerationAlarmGreySvg,
                    noAlarms: aerationAlarmGreenSvg
                },
                roof: {
                    highest: aerationRoofRedSvg,
                    hasAckAlarm: aerationRoofGreySvg,
                    noAlarms: aerationRoofGreenSvg
                },
                weather_station: {
                    highest: weatherStationRedSvg,
                    hasAlarms: weatherStationYellowSvg,
                    hasAckAlarm: weatherStationGreySvg,
                    noAlarms: weatherStationGreenSvg
                },
                relay: {
                    highest: relayRedSvg
                }
            },
            relationType: {
                aerationSystem: "AerationSystemRelation",
                relay: "RelayRelation",
                weatherStation: "WeatherStationRelation",
                modbus: "ModbusRelation",
                sms: "SMSRelation"
            },
            assetTypeTranslatePrefix: 'ag.asset.type',
            siloType: {
                cylinder: 'cylinder',
                building: 'building'
            },
            aerationType: {
                aeration: 'aeration',
                alarm: 'alarm',
                roof: 'roof'
            },
            aerationLabels: {
                aeration: 'Aeration System',
                alarm: 'Alarm System',
                roof: 'Roof Aeration'
            },
            attributes: {
                baseAsset: {
                    _label: '_label',
                    latitude: 'latitude',
                    longitude: 'longitude',
                    positionX: 'positionX', // used for 2D map
                    positionY: 'positionY', // used for 2D map
                    positionZ: 'positionZ', // used for 2D map
                    coordinate3dX: 'coordinate3dX', // used for 3D map
                    coordinate3dY: 'coordinate3dY', // used for 3D map
                    coordinate3dZ: 'coordinate3dZ', // used for 3D map
                    size: 'size'
                },
                system: {
                    imageUrl: 'imageUrl',
                    moistureCalibrations: 'moistureCalibrations',
                    useGoogleMaps: 'useGoogleMaps'
                },
                aeration: {
                    aerationType: 'aerationType',
                    disableAlarms: 'disableAlarms',
                    autoControl: 'autoControl',
                    manualControl: 'manualControl',
                    wattUsage: 'wattUsage',
                    useEMC: 'useEMC',

                    useHSTemperatureThresholds: 'useHSTemperatureThresholds',
                    startHSAerationTemperature: 'startHSAerationTemperature',
                    stopHSAerationTemperature: 'stopHSAerationTemperature',
                    
                    useHSHumidityThresholds: 'useHSHumidityThresholds',
                    startHSAerationHumidity: 'startHSAerationHumidity',
                    stopHSAerationHumidity: 'stopHSAerationHumidity',

                    useHSDewPointDifference: 'useHSDewPointDifference',
                    startHSDewPointDifference: 'startHSDewPointDifference',
                    stopHSDewPointDifference: 'stopHSDewPointDifference',
                    
                    useHSConstrain: 'useHSConstrain',
                    maxHSHumidity: 'maxHSHumidity',
                    maxHSTemperature: 'maxHSTemperature',
                    
                    
                    useTemperatureThresholds: 'useTemperatureThresholds',
                    startAerationTemperature: 'startAerationTemperature',
                    stopAerationTemperature: 'stopAerationTemperature',
                    useDeltaTemperatureThresholds: 'useDeltaTemperatureThresholds',
                    startAerationDeltaTemperature: 'startAerationDeltaTemperature',
                    stopAerationDeltaTemperature: 'stopAerationDeltaTemperature',
                    
                    useWeatherStationEmcConstrain: 'useWeatherStationEmcConstrain',
                    maxWeatherStationEmc: 'maxWeatherStationEmc',
                    minWeatherStationEmc: 'minWeatherStationEmc',
                    
                    useEmcThresholds: 'useEmcThresholds',
                    startAerationTopEmc: 'startAerationTopEmc',
                    stopAerationTopEmc: 'stopAerationTopEmc',
                    stopAerationBottomEmc: 'stopAerationBottomEmc',
                    startAerationBottomEmc: 'startAerationBottomEmc',

                    useWeatherStationConstrain: 'useWeatherStationConstrain',
                    maxWeatherStationHumidity: 'maxWeatherStationHumidity',
                    maxWeatherStationTemperature: 'maxWeatherStationTemperature',
                    useTimeFrameConstrain: 'useTimeFrameConstrain',
                    allowedTimeFrameFrom: 'allowedTimeFrameFrom',
                    allowedTimeFrameTo: 'allowedTimeFrameTo',
                    useGrainOutsideTemperatureDifference: 'useGrainOutsideTemperatureDifference',
                    minGrainOutsideDifference: 'minGrainOutsideDifference'
                },
                customer: {
                    name: "Agrolog Customer",
                },
                site: {
                    imageUrl: 'imageUrl',
                    enable3D: 'enable3D',
                },
                siloGroup: {
                    viewrotation: 'viewrotation',
                    width: 'width',
                    height: 'height',
                    length: 'length',
                    disableAlarms: 'disableAlarms',
                    imageUrl: 'imageUrl',
                    maxTelemetryDelaySec: 'maxTelemetryDelaySec'
                },
                silo: {
                    autoDisableSensors: 'autoDisableSensors',
                    disableAlarms: 'disableAlarms',
                    siloType: 'siloType',
                    diameter: 'diameter',
                    width: 'width',
                    length: 'length',
                    eaveheight: 'eaveheight',
                    totalheight: 'totalheight',
                    hopper: 'hopper',
                    hopperheight: 'hopperheight',
                    hopperangle: 'hopperangle',
                    viewrotation: 'viewrotation',
                    maxTemperature: 'maxTemperature',
                    maxDeltaTemperature: 'maxDeltaTemperature',
                    maxMoisture: 'maxMoisture',
                    maxDeltaMoisture: 'maxDeltaMoisture',
                    maxCO2: 'maxCO2',
                    maxDeltaCO2: 'maxDeltaCO2',
                    maxTelemetryDelaySec: 'maxTelemetryDelaySec',
                    enabledSensors: 'enabledSensors',
                    hasMoistureCables: 'hasMoistureCables',
                    hasCO2Cables: 'hasCO2Cables',
                    hasLevelIndicator: 'hasLevelIndicator',
                    hasHeadSpaceSensor: 'hasHeadSpaceSensor',
                    maxCropLevel: 'maxCropLevel',
                    emptyCropLevel: 'emptyCropLevel',
                    fullCropLevel: 'fullCropLevel',
                    customCropDensity: 'customCropDensity',
                    customCropGlidingAngle: 'customCropGlidingAngle',
                    maxCropLevelThreshold: 'maxCropLevelThreshold',
                    minCropLevelThreshold: 'minCropLevelThreshold',
                    maxHeadSpaceTemperatureThreshold: 'maxHeadSpaceTemperatureThreshold',
                    maxHeadSpaceHumidityThreshold: 'maxHeadSpaceHumidityThreshold',
                    maxHeadSpaceCo2WarningThreshold: 'maxHeadSpaceCo2WarningThreshold',
                    maxHeadSpaceCo2CriticalThreshold: 'maxHeadSpaceCo2CriticalThreshold',
                    minHeadSpaceDewpointTemperatureDiffThreshold: 'minHeadSpaceDewpointTemperatureDiffThreshold'
                },
                spearSensor: {
                    latitude: "latitude",
                    longitude: "longitude"
                },
                weatherStation: {
                    disableAlarms: 'disableAlarms',
                    maxTelemetryDelaySec: 'maxTelemetryDelaySec',
                    viewrotation: 'viewrotation',
                    size: 'size'
                },
                weatherStationDevice: {
                    deviceType: 'deviceType',
                    address: 'address',
                    config: 'config'
                },
                headSpaceSensor: {
                    deviceType: 'deviceType',
                    address: 'address',
                    config: 'config'
                },
                temperatureSensorLinesServer: {
                    length: 'length',
                    sensorDistance: 'sensorDistance',
                    firstSensorOffset: 'firstSensorOffset',
                    positionX: 'positionX',
                    positionY: 'positionY',
                    positionZ: 'positionZ'
                },
                temperatureSensorLinesClient: {
                    lineNum: 'lineNum',
                    sensorCount: 'sensorCount',
                    moistureSensors: 'moistureSensors',
                    co2Sensors: 'co2Sensors'
                },
                levelIndicatorServer: {
                    mode: 'mode',
                    measurePoints: 'measurePoints',
                    deviceType: 'deviceType',
                    address: 'address',
                    config: 'config'
                },
                levelIndicatorClient: {
                    status: 'status',
                    blocked: 'blocked',
                    cycleTime: 'cycle_time'
                },
                levelIndicatorShared: {
                    blocked: 'blocked',
                    cycleTime: 'cycle_time'
                },
                relay: {
                    relayType: "relayType",
                    address: "address",
                    channelsNum: "channelsNum"
                },
                relayRelation: {
                    channelIds: 'channelIds'
                },
                aerationSystemRelation: {
                },
                gateway: {
                    subsysConfig: 'subsysConfig'
                },
                sms: {
                    subsysConfig: 'subsysConfig'
                }
            },
            command: {
                sms: {
                    reconfigure: {
                        name: 'ag.command.sms.reconfigure',
                        value: 'reconfigure',
                        hasNcId: true
                    },
                    set_nc_address: {
                        name: 'ag.command.sms.set-nc-address',
                        value: 'set_nc_address',
                        hasNcId: true
                    },
                    set_serial: {
                        name: 'ag.command.sms.set-serial',
                        value: 'set_serial',
                        hasSerial: true,
                        hasNcId: true
                    },
                    get_serial: {
                        name: 'ag.command.sms.get-serial',
                        value: 'get_serial',
                        hasNcId: true
                    },
                    ping_nc: {
                        name: 'ag.command.sms.ping-nc',
                        value: 'ping_nc',
                        hasNcId: true
                    },
                    ping_line: {
                        name: 'ag.command.sms.ping-line',
                        value: 'ping_line',
                        hasLineId: true,
                        hasNcId: true
                    },
                    clear_nc: {
                        name: 'ag.command.sms.clear-nc',
                        value: 'clear_nc',
                        hasNcId: true
                    },
                    get_config: {
                        name: 'ag.command.sms.get-nc-config',
                        value: 'get_config',
                        hasNcId: true
                    },
                    get_config_object: {
                        name: 'ag.command.sms.get-config-object',
                        value: 'get_config_object'
                    },
                    get_temperature: {
                        name: 'ag.command.sms.get-line-temperature',
                        value: 'get_temperature',
                        hasLineId: true,
                        hasNcId: true
                    },
                    controllercmd_list_cloud_devices: {
                        name: 'ag.command.controllercmd-list-cloud-devices',
                        value: 'controllercmd_list_cloud_devices'
                    }
                }
            },
            customerHostanmes: {
                tornum: "tornum.agrolog.io",
                chief: "chief.agrolog.io",
                cimbria: "cimbria.agrolog.io",
                agi: "agi.agrolog.io",
                mysilo: "mysilo.agrolog.io",
                pradosilos: "prado.agrolog.io",
                farmer: "panel.agrolog.io"
            },
            units: {
              humidity: "%",
              moisture: "%",
              cropLevel: "m",
              pressure: "kPa",
              co2Level: "ppm"
            },
            telemetry: {
                silo: {
                    currentMinTemperature: 'current_min_temperature',
                    currentAvgTemperature: 'current_avg_temperature',
                    currentMaxTemperature: 'current_max_temperature',
                    currentMinDeltaTemperature: 'current_min_delta_temperature',
                    currentAvgDeltaTemperature: 'current_avg_delta_temperature',
                    currentMaxDeltaTemperature: 'current_max_delta_temperature',
                    currentMinMoisture: 'current_min_moisture',
                    currentAvgMoisture: 'current_avg_moisture',
                    currentMaxMoisture: 'current_max_moisture',
                    currentMinDeltaMoisture: 'current_min_delta_moisture',
                    currentAvgDeltaMoisture: 'current_avg_delta_moisture',
                    currentMaxDeltaMoisture: 'current_max_delta_moisture',
                    currentMinCO2: 'current_min_co2',
                    currentAvgCO2: 'current_avg_co2',
                    currentMaxCO2: 'current_max_co2',
                    currentMinDeltaCO2: 'current_min_delta_co2',
                    currentAvgDeltaCO2: 'current_avg_delta_co2',
                    currentMaxDeltaCO2: 'current_max_delta_co2',
                    deactivationLevel: 'deactivation_level',
                    cropType: 'crop_type',
                    cropLevelMin: 'crop-level-min',
                    cropLevelAvg: 'crop-level-avg',
                    cropLevelMax: 'crop-level-max',
                    cropPoints: "crop-points",
                    cropVolume: "crop-volume",
                    cropWeight: "crop-weight",
                    cropDensity: "crop-density",
                    siloVolume: "silo-volume"
                },
                siloGroup: {
                    currentMinTemperature: 'current_min_temperature',
                    currentAvgTemperature: 'current_avg_temperature',
                    currentMaxTemperature: 'current_max_temperature',
                    currentMinDeltaTemperature: 'current_min_delta_temperature',
                    currentAvgDeltaTemperature: 'current_avg_delta_temperature',
                    currentMaxDeltaTemperature: 'current_max_delta_temperature',
                    currentMinMoisture: 'current_min_moisture',
                    currentAvgMoisture: 'current_avg_moisture',
                    currentMaxMoisture: 'current_max_moisture',
                    currentMinDeltaMoisture: 'current_min_delta_moisture',
                    currentAvgDeltaMoisture: 'current_avg_delta_moisture',
                    currentMaxDeltaMoisture: 'current_max_delta_moisture',
                    currentMinCO2: 'current_min_co2',
                    currentAvgCO2: 'current_avg_co2',
                    currentMaxCO2: 'current_max_co2',
                    currentMinDeltaCO2: 'current_min_delta_co2',
                    currentAvgDeltaCO2: 'current_avg_delta_co2',
                    currentMaxDeltaCO2: 'current_max_delta_co2',
                },
                sensorLines: {
                    deltaTemperaturePrefix: 'delta-temperature-',
                    temperaturePrefix: 'temperature-',
                    deltaMoisturePrefix: 'delta-moisture-',
                    moisturePrefix: 'moisture-',
                    deltaCO2Prefix: 'delta-co2-',
                    co2Prefix: 'co2-',
                    temperatureErrorPrefix: 'temperature-error-',
                    moistureErrorPrefix: 'moisture-error-',
                    co2ErrorPrefix: 'co2-error-',
                    emcPrefix: 'EMC-'
                },
                spearSensor: {
                    temperature: "temperature_1",
                    ambientTemperature: "temperature_ambient"
                },
                levelIndicator: {
                    airLevelPrefix: 'air-level-',
                    cropLevelPrefix: 'crop-level-'
                },
                headSpaceSensor: {
                    temperature: "temperature",
                    humidity: "humidity",
                    dewpoint: "dewpoint",
                    co2level: "co2_level",
                    pressure: "pressure"
                },
                weatherStation: {
                    temperature: 'temperature',
                    moisture: 'moisture',
                    humidity: 'humidity'
                },
                aeration: {
                    state: 'state'
                },
                type: {
                    temperature: 'temperature',
                    moisture: 'moisture',
                    co2: 'co2'
                }
            },
            loggingLevel: {
                critical: 'CRITICAL',
                error: 'ERROR',
                warning: 'WARNING',
                info: 'INFO',
                debug: 'DEBUG'
            },
            levelIndicatorTypes: {
                default: "niveaubob4000",
                additional: "HR80G"
            },
            weatherStationTypes: {
                additional: "WS-RK330-01",
                default: "WS-RK330-01V2"
            },
            headSpaceSensorTypes: {
                default: "EE872",
                hs2000: "HS2000"
            },
            defaults: {
                silo: {
                    maxTelemetryDelaySec: 7200,
                    maxTemperature: 50,
                    maxDeltaTemperature: 50,
                    maxMoisture: 50,
                    maxDeltaMoisture: 50,
                    maxCO2: 2000,
                    maxDeltaCO2: 2000,
                    maxHeadSpaceTemperatureThreshold: 60,
                    maxHeadSpaceHumidityThreshold: 90,
                    maxHeadSpaceCo2WarningThreshold: 3000,
                    maxHeadSpaceCo2CriticalThreshold: 5000,
                    minHeadSpaceDewpointTemperatureDiffThreshold: 3,
                },
                siloGroup: {
                    maxTelemetryDelaySec: 3600
                },
                weatherStation: {
                    maxTelemetryDelaySec: 3600
                }
            },
            adminState: {
                system: 'system',
                sites: 'sites',
                site: 'site',
                silo: 'silo',
                siloGroup: 'silo_group',
                aeration: 'aeration',
                weatherStation: 'weather_station'
            },
            clientState: {
                system: 'system',
                site: 'site',
                silo: 'silo',
                siloGroup: 'silo_group',
                siloGroupSilo: 'silo_group_silo',
                siloGroupAeration: 'silo_group_aeration',
                siloGroupWeatherStation: 'silo_group_weather_station',
                siloGroupMultiSettings: 'silo_group_multi_settings',
                aeration: 'aeration',
                weatherStation: 'weather_station',
                multiSettings: 'multi_settings',
                assetStates: {
                    'site': 'site',
                    'silo_group': 'silo_group',
                    'silo': 'silo',
                    'aeration': 'aeration',
                    'weather_station': 'weather_station'
                },
                siloGroupAssetStates: {
                    'silo': 'silo_group_silo',
                    'aeration': 'silo_group_aeration',
                    'weather_station': 'silo_group_weather_station'
                }
            },
            deviceType: {
                temperatureSensorLines: 'temperature_sensor_lines',
                moistureSensorLines: 'moisture_sensor_lines',
                co2SensorLines: 'co2_sensor_lines',
                levelIndicator: 'level_indicator',
                headSpaceSensor: 'head_space_sensor',
                headSpaceSensor2000: 'head_space_sensor_2000',
                weatherStation: 'weather_station',
                relay: 'relay',
                relaySystem: "relay_system",
                gateway: 'gateway',
                sms: 'SMS',
                modbus: "modbus",
                generic: "generic",
                default: 'default'
            },
            farmerDeviceTypes: {
                cellularSensorSpear: {
                    name: "Sensors Spear",
                    icon: "album",
                    value: "cellular_sensor_spear"
                },
                cPro: {
                    name: "C-Pro",
                    value: "c_pro"
                },
                superPro: {
                    name: "SuperPro",
                    value: "super_pro"
                },
                farmPro: {
                    name: "FarmPro",
                    value: "farm_pro"
                },
                superPoint: {
                    name: "SuperPoint",
                    value: "super_point"
                },
                farmPoint: {
                    name: "FarmPoint",
                    value: "farm_point"
                }
            },
            deviceNamePrefix: {
                temperatureSensorLines: 'SLT_PN-',
                moistureSensorLines: 'SLM_PN-',
                co2SensorLines: 'SLCO2_PN-',
                levelIndicator: 'LIN_PN-',
                headSpaceSensor: 'HSS_PN-',
                weatherStation: 'WSHT_PN-',
                relay: 'RELAY_PN-',
                relaySystem: "RS_PN-",
                gateway: 'GW_TN-',
                sms: 'SMS_PN-',
                modbus: 'MODBUS_PN-'
            },
            deviceNameSuffix: {
                temperatureSensorLines: '_L-',
                moistureSensorLines: '_L-',
                co2SensorLines: '_L-',
                levelIndicator: '_A-',
                headSpaceSensor: '_A-',
                weatherStation: '_N-',
                relay: '_A-',
                relaySystem: '_N-',
                gateway: '_GN-',
                sms: '_N-',
                modbus: '_N-'
            },
            netControllerStatus: {
                done: 'ag.sms.nc-status-done',
                waiting: 'ag.sms.nc-status-waiting',
                configuring: 'ag.sms.nc-status-configuring'
            },
            deviceTypeTranslatePrefix: 'ag.device.type',
            assetBrowserWidgetType: {
                list: 'ag.asset-browser.list',
                map: 'ag.asset-browser.map',
                dashboard: 'ag.asset-browser.dashboard',
                threeDimensional: 'ag.asset-browser.threeDimensional'
            },
            systemWidgetType: {
                systemDetails: 'ag.system.system-details',
                systemSettings: 'ag.system.system-settings'
            },
            siteWidgetType: {
                siteDetails: 'ag.site.site-details',
                siteAlarms: 'ag.site.site-alarms',
                siteSettings: 'ag.site.site-settings'
            },
            siloWidgetType: {
                siloOverview: 'ag.silo.silo-overview',
                siloTemperatureDetails: 'ag.silo.silo-temperature-details',
                siloMoistureDetails: 'ag.silo.silo-moisture-details',
                siloCO2Details: 'ag.silo.silo-co2-details',
                siloCropLevel: 'ag.silo.silo-crop-level',
                siloHeadSpace: 'ag.silo.silo-head-space',
                siloSensorsHistory: 'ag.silo.silo-sensors-history',
                siloAlarms: 'ag.silo.silo-alarms',
                siloConnectedSystems: 'ag.silo.silo-connected-systems',
                siloSettings: 'ag.silo.silo-settings',
                siloComments: 'ag.silo.silo-comments'
            },
            siloGroupWidgetType: {
                siloGroupOverview: 'ag.silo-group.silo-group-overview',
                siloGroupTemperatureDetails: 'ag.silo-group.silo-group-temperature-details',
                siloGroupMoistureDetails: 'ag.silo-group.silo-group-moisture-details',
                siloGroupCO2Details: 'ag.silo-group.silo-group-co2-details',
                siloGroupAlarms: 'ag.silo-group.silo-group-alarms',
                siloGroupSettings: 'ag.silo-group.silo-group-settings',
                siloGroupComments: 'ag.silo-group.silo-group-comments'
            },
            aerationWidgetType: {
                aerationDetails: 'ag.aeration.aeration-details',
                aerationAlarms: 'ag.aeration.aeration-alarms',
                aerationConnectedSystems: 'ag.aeration.aeration-connected-systems',
                aerationSettings: 'ag.aeration.aeration-settings',
                aerationComments: 'ag.aeration.aeration-comments'
            },
            weatherStationWidgetType: {
                weatherStationDetails: 'ag.weather-station.weather-station-details',
                weatherStationAlarms: 'ag.weather-station.weather-station-alarms',
                weatherStationSettings: 'ag.weather-station.weather-station-settings',
                weatherStationComments: 'ag.weather-station.weather-station-comments'
            },
            alarmType: {
                AERATION_STATE_ALARM: 'ag.alarm.aeration-state-alarm',
                AERATION_ALARM: 'ag.alarm.aeration-alarm',
                NO_TELEMETRY_ALARM: 'ag.alarm.no-telemetry-alarm',
                TEMPERATURE_ALARM: 'ag.alarm.temperature-alarm',
                ERROR_VALUES_ALARM: 'ag.alarm.error-values-alarm',
                DELTA_TEMPERATURE_ALARM: 'ag.alarm.delta-temperature-alarm',
                NO_WEATHER_STATION_TELEMETRY_ALARM: 'ag.alarm.no-weather-station-telemetry-alarm',
                MOISTURE_ALARM: 'ag.alarm.moisture-alarm',
                DELTA_MOISTURE_ALARM: 'ag.alarm.delta-moisture-alarm',
                CO2_ALARM: 'ag.alarm.co2-alarm',
                DELTA_CO2_ALARM: 'ag.alarm.delta-co2-alarm',
                RELAY_IS_NOT_RESPONDING_ALARM: 'ag.alarm.relay-is-not-responding-alarm',
                HEAD_SPACE_TEMPERATURE_ALARM: 'ag.alarm.head-space-temperature-alarm',
                HEAD_SPACE_HUMIDITY_ALARM: 'ag.alarm.head-space-humidity-alarm',
                HEAD_SPACE_CO2_LEVEL_ALARM_WARNING: 'ag.alarm.head-space-co2-level-warning-alarm',
                HEAD_SPACE_CO2_LEVEL_ALARM_CRITICAL: 'ag.alarm.head-space-co2-level-critical-alarm',
                HEAD_SPACE_DEWPOINT_ALARM: 'ag.alarm.head-space-dewpoint-alarm',
                MIN_CROP_LEVEL_ALARM: 'ag.alarm.level-indicator-min-crop-level-alarm',
                MAX_CROP_LEVEL_ALARM: 'ag.alarm.level-indicator-max-crop-level-alarm'
            },
            spearWidgetType: {
                "deviceOverview": "deviceOverview",
                "deviceSettings": "deviceSettings",
                "deviceNotes": "deviceNotes"
            },
            manualWidgetType: {
                "measurements": "measurements",
                "mapMeasurements": "mapMeasurements"
            },
            docsLanguages: {
                en_US: "en",
                ru_RU: "ru"
            },
            cropNames: {
                "Oil Rape Seed": "ag.silo.crop-names.oil-rape-seed",
                "Oats": "ag.silo.crop-names.oats",
                "Corn": "ag.silo.crop-names.corn",
                "Wheat": "ag.silo.crop-names.wheat",
                "Durum Wheat": "ag.silo.crop-names.durum-wheat",
                "Barley": "ag.silo.crop-names.barley",
                "Sorghum": "ag.silo.crop-names.sorghum",
                "Soy Bean": "ag.silo.crop-names.soy-bean",
                "Rice": "ag.silo.crop-names.rice",
                "Rye": "ag.silo.crop-names.rye",
                "Bean - Soy": "ag.silo.crop-names.bean-soy",
                "Bean - Black": "ag.silo.crop-names.bean-black",
                "Bean - Broad": "ag.silo.crop-names.bean-broad",
                "Bean - Kidney": "ag.silo.crop-names.bean-kidney",
                "Bean - Pinto": "ag.silo.crop-names.bean-pinto",
                "Canary Seed": "ag.silo.crop-names.canary-seed",
                "Flax Seed": "ag.silo.crop-names.flax-seed",
                "Grass Seeds": "ag.silo.crop-names.grass-seeds",
                "Lentils": "ag.silo.crop-names.lentils",
                "Maize": "ag.silo.crop-names.maize",
                "Mustard Oriental": "ag.silo.crop-names.mustard-oriental",
                "Oil Rape Seed - Canola": "ag.silo.crop-names.oil-rape-canola",
                "Peanut": "ag.silo.crop-names.peanut",
                "Peas - Chickpeas": "ag.silo.crop-names.peas-chickpeas",
                "Peas - Yellow": "ag.silo.crop-names.peas-yellow",
                "Pistachio": "ag.silo.crop-names.pistachio",
                "Sunflower": "ag.silo.crop-names.sunflower",
                "Sunflower Oil": "ag.silo.crop-names.sunflower-oil",
                "Wheat - Soft": "ag.silo.crop-names.wheat-soft",
                "Wheat - Durum": "ag.silo.crop-names.wheat-durum",
                "Yellow Mustard": "ag.silo.crop-names.yellow-mustard",
            },
            farmerCropNames: {"alfalfa":"Alfalfa","barley":"Barley","bean_uk":"Bean UK","black_bean":"Black bean","black_eyed_pea":"Black eyed pea","blue_grass":"Blue grass","broad_bean":"Broad bean","brown_rice_short_and_long":"Brown rice short and long","buck_wheat":"Buck wheat","canary_seed":"Canary seed","caraway":"Caraway","chickpeas":"Chickpeas","chineese_cabbage":"Chineese cabbage","chives":"Chives","cocksfoot":"Cocksfoot","cocoa":"Cocoa","coffee_arabica":"Coffee arabica","coffee_robusta":"Coffee robusta","coriander":"Coriander","dodder_seed":"Dodder seed","durum_wheat":"Durum wheat","emmer_wheat":"Emmer wheat","fennel":"Fennel","green_grams":"Green grams","hazel_nut":"Hazel nut","hemp_seed":"Hemp seed","indian_rice_broken":"Indian rice broken","indian_PSB_feed_202_prestarter":"Indian PSB feed 202 prestarter","indian_basmati_paddy_rice":"Indian basmati paddy rice","indian_fin_b_feed_303_finish":"Indian fin b feed 303 finish","indian_mdoc_brown_oil_cake_mustard":"Indian mdoc brown oil cake mustard","indian_meat_bone_meal":"Indian meat bone meal","indian_non_basmati_paddy_raw":"Indian non basmati paddy raw","indian_paddy_parboiled_and_dry":"Indian paddy parboiled and dry","indian_soya_doc":"Indian soya doc","indian_starter_b_feed_503_starter":"Indian starter b feed 503 starter","lentil":"Lentil","lin_seed":"Lin seed","lupin":"Lupin","maise":"Maise","meadow_fesque":"Meadow fesque","millet":"Millet","mustard_seed":"Mustard seed","oats":"Oats","paddy_rice_short":"Paddy rice short","paddy_rice_long":"Paddy rice long","parsley":"Parsley","peanuts":"Peanuts","peas_green_fine":"Peas green fine","pigeon_pea":"Pigeon pea","poppy":"Poppy","pumpkin_seed":"Pumpkin seed","quinoa":"Quinoa","radish":"Radish","rybs":"Rybs","rape":"Rape","rape_cake":"Rape cake","red_clover":"Red clover","red_fesque":"Red fesque","russian_barley":"Russian barley","russian_buck_wheat":"Russian buck wheat","russian_maise":"Russian maise","russian_oats":"Russian oats","russian_paddy_rice_short":"Russian paddy rice short","russian_peas":"Russian peas","russian_rye":"Russian rye","russian_sunflower":"Russian sunflower","russian_wheat":"Russian wheat","rye":"Rye","rye_flour":"Rye flour","rye_grass":"Rye grass","sesame":"Sesame","sorghum":"Sorghum","sorrel":"Sorrel","soy_bean":"Soy bean","soy_schred":"Soy schred","soya_meal":"Soya meal","spelt":"Spelt","spinach_round":"Spinach round","spinach_pointed":"Spinach pointed","sun_cake":"Sun cake","sun_extract_meal":"Sun extract meal","sunflower":"Sunflower","tall_fescue":"Tall fescue","timothy":"Timothy","triticale":"Triticale","wheat":"Wheat","wheat_flour":"Wheat flour","white_clover":"White clover","white_rice":"White rice","yellow_peas":"Yellow peas"},
            cablesPositionProperties: [
                {
                    siloDiameter: 4,
                    zeroRingLinesCount: 1
                }, {
                    siloDiameter: 7,
                    zeroRingLinesCount: 1
                }, {
                    siloDiameter: 7.5,
                    firstRingRadius: 2.1,
                    firstRingLinesCount: 3,
                    firstRingAngle: 120
                }, {
                    siloDiameter: 8,
                    firstRingRadius: 2.3,
                    firstRingLinesCount: 3,
                    firstRingAngle: 120
                }, {
                    siloDiameter: 8.5,
                    firstRingRadius: 2.3,
                    firstRingLinesCount: 3,
                    firstRingAngle: 120
                }, {
                    siloDiameter: 9,
                    firstRingRadius: 2.4,
                    firstRingLinesCount: 3,
                    firstRingAngle: 120
                }, {
                    siloDiameter: 9.5,
                    firstRingRadius: 2.4,
                    firstRingLinesCount: 3,
                    firstRingAngle: 120
                }, {
                    siloDiameter: 10,
                    firstRingRadius: 2.5,
                    firstRingLinesCount: 3,
                    firstRingAngle: 120
                }, {
                    siloDiameter: 10.5,
                    firstRingRadius: 2.6,
                    firstRingLinesCount: 3,
                    firstRingAngle: 120
                }, {
                    siloDiameter: 11,
                    firstRingRadius: 2.8,
                    firstRingLinesCount: 3,
                    firstRingAngle: 120
                }, {
                    siloDiameter: 11.5,
                    firstRingRadius: 3,
                    firstRingLinesCount: 3,
                    firstRingAngle: 120
                }, {
                    siloDiameter: 12,
                    zeroRingLinesCount: 1,
                    firstRingRadius: 3.3,
                    firstRingLinesCount: 3,
                    firstRingAngle: 120
                }, {
                    siloDiameter: 12.5,
                    zeroRingLinesCount: 1,
                    firstRingRadius: 3.6,
                    firstRingLinesCount: 3,
                    firstRingAngle: 120
                }, {
                    siloDiameter: 13,
                    zeroRingLinesCount: 1,
                    firstRingRadius: 4,
                    firstRingLinesCount: 3,
                    firstRingAngle: 120
                }, {
                    siloDiameter: 13.5,
                    zeroRingLinesCount: 1,
                    firstRingRadius: 4.3,
                    firstRingLinesCount: 3,
                    firstRingAngle: 120
                }, {
                    siloDiameter: 14,
                    zeroRingLinesCount: 1,
                    firstRingRadius: 4.7,
                    firstRingLinesCount: 5,
                    firstRingAngle: 72
                }, {
                    siloDiameter: 14.5,
                    zeroRingLinesCount: 1,
                    firstRingRadius: 4.9,
                    firstRingLinesCount: 5,
                    firstRingAngle: 72
                }, {
                    siloDiameter: 15,
                    zeroRingLinesCount: 1,
                    firstRingRadius: 5.2,
                    firstRingLinesCount: 5,
                    firstRingAngle: 72
                }, {
                    siloDiameter: 15.5,
                    zeroRingLinesCount: 1,
                    firstRingRadius: 5.4,
                    firstRingLinesCount: 5,
                    firstRingAngle: 72
                }, {
                    siloDiameter: 16,
                    zeroRingLinesCount: 1,
                    firstRingRadius: 5.6,
                    firstRingLinesCount: 6,
                    firstRingAngle: 60
                }, {
                    siloDiameter: 16.5,
                    zeroRingLinesCount: 1,
                    firstRingRadius: 5.7,
                    firstRingLinesCount: 6,
                    firstRingAngle: 60
                }, {
                    siloDiameter: 17,
                    zeroRingLinesCount: 1,
                    firstRingRadius: 5.8,
                    firstRingLinesCount: 6,
                    firstRingAngle: 60
                }, {
                    siloDiameter: 17.5,
                    zeroRingLinesCount: 1,
                    firstRingRadius: 5.9,
                    firstRingLinesCount: 6,
                    firstRingAngle: 60
                }, {
                    siloDiameter: 18,
                    zeroRingLinesCount: 1,
                    firstRingRadius: 6,
                    firstRingLinesCount: 7,
                    firstRingAngle: 51.4
                }, {
                    siloDiameter: 18.5,
                    zeroRingLinesCount: 1,
                    firstRingRadius: 6.1,
                    firstRingLinesCount: 7,
                    firstRingAngle: 51.4
                }, {
                    siloDiameter: 19,
                    zeroRingLinesCount: 1,
                    firstRingRadius: 6.1,
                    firstRingLinesCount: 7,
                    firstRingAngle: 51.4
                }, {
                    siloDiameter: 19.5,
                    zeroRingLinesCount: 1,
                    firstRingRadius: 6.2,
                    firstRingLinesCount: 7,
                    firstRingAngle: 51.4
                }, {
                    siloDiameter: 20,
                    firstRingRadius: 2.5,
                    firstRingLinesCount: 3,
                    firstRingAngle: 120,
                    secondRingRadius: 7.5,
                    secondRingLinesCount: 8,
                    secondRingAngle: 45
                }, {
                    siloDiameter: 20.5,
                    firstRingRadius: 2.7,
                    firstRingLinesCount: 3,
                    firstRingAngle: 120,
                    secondRingRadius: 7.6,
                    secondRingLinesCount: 8,
                    secondRingAngle: 45
                }, {
                    siloDiameter: 21,
                    firstRingRadius: 2.7,
                    firstRingLinesCount: 3,
                    firstRingAngle: 120,
                    secondRingRadius: 7.8,
                    secondRingLinesCount: 8,
                    secondRingAngle: 45
                }, {
                    siloDiameter: 21.5,
                    firstRingRadius: 2.8,
                    firstRingLinesCount: 3,
                    firstRingAngle: 120,
                    secondRingRadius: 8,
                    secondRingLinesCount: 8,
                    secondRingAngle: 45
                }, {
                    siloDiameter: 22,
                    firstRingRadius: 2.8,
                    firstRingLinesCount: 3,
                    firstRingAngle: 120,
                    secondRingRadius: 8.2,
                    secondRingLinesCount: 9,
                    secondRingAngle: 40
                }, {
                    siloDiameter: 22.5,
                    firstRingRadius: 2.8,
                    firstRingLinesCount: 3,
                    firstRingAngle: 120,
                    secondRingRadius: 8.3,
                    secondRingLinesCount: 9,
                    secondRingAngle: 40
                }, {
                    siloDiameter: 23,
                    firstRingRadius: 2.8,
                    firstRingLinesCount: 3,
                    firstRingAngle: 120,
                    secondRingRadius: 8.5,
                    secondRingLinesCount: 9,
                    secondRingAngle: 40
                }, {
                    siloDiameter: 23.5,
                    firstRingRadius: 3,
                    firstRingLinesCount: 3,
                    firstRingAngle: 120,
                    secondRingRadius: 8.8,
                    secondRingLinesCount: 9,
                    secondRingAngle: 40
                }, {
                    siloDiameter: 24,
                    firstRingRadius: 3,
                    firstRingLinesCount: 3,
                    firstRingAngle: 120,
                    secondRingRadius: 9,
                    secondRingLinesCount: 10,
                    secondRingAngle: 36
                }, {
                    siloDiameter: 24.5,
                    firstRingRadius: 3.2,
                    firstRingLinesCount: 3,
                    firstRingAngle: 120,
                    secondRingRadius: 9.3,
                    secondRingLinesCount: 10,
                    secondRingAngle: 36
                }, {
                    siloDiameter: 25,
                    firstRingRadius: 3.2,
                    firstRingLinesCount: 3,
                    firstRingAngle: 120,
                    secondRingRadius: 9.6,
                    secondRingLinesCount: 10,
                    secondRingAngle: 36
                }, {
                    siloDiameter: 25.5,
                    firstRingRadius: 3.5,
                    firstRingLinesCount: 3,
                    firstRingAngle: 120,
                    secondRingRadius: 10,
                    secondRingLinesCount: 10,
                    secondRingAngle: 36
                }, {
                    siloDiameter: 26,
                    zeroRingLinesCount: 1,
                    firstRingRadius: 5.3,
                    firstRingLinesCount: 6,
                    firstRingAngle: 60,
                    secondRingRadius: 10.6,
                    secondRingLinesCount: 10,
                    secondRingAngle: 36
                }, {
                    siloDiameter: 26.5,
                    zeroRingLinesCount: 1,
                    firstRingRadius: 5.3,
                    firstRingLinesCount: 6,
                    firstRingAngle: 60,
                    secondRingRadius: 10.6,
                    secondRingLinesCount: 10,
                    secondRingAngle: 36
                }, {
                    siloDiameter: 27,
                    zeroRingLinesCount: 1,
                    firstRingRadius: 5.3,
                    firstRingLinesCount: 6,
                    firstRingAngle: 60,
                    secondRingRadius: 10.6,
                    secondRingLinesCount: 10,
                    secondRingAngle: 36
                }, {
                    siloDiameter: 27.5,
                    zeroRingLinesCount: 1,
                    firstRingRadius: 5.3,
                    firstRingLinesCount: 6,
                    firstRingAngle: 60,
                    secondRingRadius: 10.6,
                    secondRingLinesCount: 10,
                    secondRingAngle: 36
                }, {
                    siloDiameter: 28,
                    zeroRingLinesCount: 1,
                    firstRingRadius: 5.5,
                    firstRingLinesCount: 6,
                    firstRingAngle: 60,
                    secondRingRadius: 10.6,
                    secondRingLinesCount: 12,
                    secondRingAngle: 30
                }, {
                    siloDiameter: 28.5,
                    zeroRingLinesCount: 1,
                    firstRingRadius: 5.5,
                    firstRingLinesCount: 6,
                    firstRingAngle: 60,
                    secondRingRadius: 10.6,
                    secondRingLinesCount: 12,
                    secondRingAngle: 30
                }, {
                    siloDiameter: 29,
                    zeroRingLinesCount: 1,
                    firstRingRadius: 5.5,
                    firstRingLinesCount: 6,
                    firstRingAngle: 60,
                    secondRingRadius: 10.6,
                    secondRingLinesCount: 12,
                    secondRingAngle: 30
                }, {
                    siloDiameter: 29.5,
                    zeroRingLinesCount: 1,
                    firstRingRadius: 5.5,
                    firstRingLinesCount: 6,
                    firstRingAngle: 60,
                    secondRingRadius: 10.6,
                    secondRingLinesCount: 12,
                    secondRingAngle: 30
                }, {
                    siloDiameter: 30,
                    firstRingRadius: 2.6,
                    firstRingLinesCount: 3,
                    firstRingAngle: 120,
                    secondRingRadius: 7.5,
                    secondRingLinesCount: 7,
                    secondRingAngle: 51.4,
                    thirdRingRadius: 12.8,
                    thirdRingLinesCount: 12,
                    thirdRingAngle: 30,
                }, {
                    siloDiameter: 30.5,
                    firstRingRadius: 2.6,
                    firstRingLinesCount: 3,
                    firstRingAngle: 120,
                    secondRingRadius: 7.5,
                    secondRingLinesCount: 7,
                    secondRingAngle: 51.4,
                    thirdRingRadius: 12.9,
                    thirdRingLinesCount: 12,
                    thirdRingAngle: 30,
                }, {
                    siloDiameter: 31,
                    firstRingRadius: 2.6,
                    firstRingLinesCount: 3,
                    firstRingAngle: 120,
                    secondRingRadius: 7.5,
                    secondRingLinesCount: 7,
                    secondRingAngle: 51.4,
                    thirdRingRadius: 13,
                    thirdRingLinesCount: 12,
                    thirdRingAngle: 30,
                }, {
                    siloDiameter: 31.5,
                    firstRingRadius: 2.6,
                    firstRingLinesCount: 3,
                    firstRingAngle: 120,
                    secondRingRadius: 7.6,
                    secondRingLinesCount: 7,
                    secondRingAngle: 51.4,
                    thirdRingRadius: 13.2,
                    thirdRingLinesCount: 12,
                    thirdRingAngle: 30,
                }, {
                    siloDiameter: 32,
                    firstRingRadius: 2.6,
                    firstRingLinesCount: 3,
                    firstRingAngle: 120,
                    secondRingRadius: 7.6,
                    secondRingLinesCount: 7,
                    secondRingAngle: 51.4,
                    thirdRingRadius: 13.3,
                    thirdRingLinesCount: 12,
                    thirdRingAngle: 30,
                }, {
                    siloDiameter: 32.5,
                    firstRingRadius: 2.7,
                    firstRingLinesCount: 3,
                    firstRingAngle: 120,
                    secondRingRadius: 7.7,
                    secondRingLinesCount: 8,
                    secondRingAngle: 45,
                    thirdRingRadius: 13.5,
                    thirdRingLinesCount: 14,
                    thirdRingAngle: 25.7,
                }, {
                    siloDiameter: 33,
                    firstRingRadius: 2.7,
                    firstRingLinesCount: 3,
                    firstRingAngle: 120,
                    secondRingRadius: 7.7,
                    secondRingLinesCount: 8,
                    secondRingAngle: 45,
                    thirdRingRadius: 13.7,
                    thirdRingLinesCount: 14,
                    thirdRingAngle: 25.7,
                }, {
                    siloDiameter: 33.5,
                    firstRingRadius: 2.7,
                    firstRingLinesCount: 3,
                    firstRingAngle: 120,
                    secondRingRadius: 8,
                    secondRingLinesCount: 8,
                    secondRingAngle: 45,
                    thirdRingRadius: 13.9,
                    thirdRingLinesCount: 14,
                    thirdRingAngle: 25.7,
                }, {
                    siloDiameter: 34,
                    firstRingRadius: 2.8,
                    firstRingLinesCount: 3,
                    firstRingAngle: 120,
                    secondRingRadius: 8,
                    secondRingLinesCount: 8,
                    secondRingAngle: 45,
                    thirdRingRadius: 14.1,
                    thirdRingLinesCount: 14,
                    thirdRingAngle: 25.7,
                }, {
                    siloDiameter: 34.5,
                    firstRingRadius: 2.8,
                    firstRingLinesCount: 3,
                    firstRingAngle: 120,
                    secondRingRadius: 8,
                    secondRingLinesCount: 8,
                    secondRingAngle: 45,
                    thirdRingRadius: 14.3,
                    thirdRingLinesCount: 14,
                    thirdRingAngle: 25.7,
                }, {
                    siloDiameter: 35,
                    firstRingRadius: 2.8,
                    firstRingLinesCount: 3,
                    firstRingAngle: 120,
                    secondRingRadius: 8.5,
                    secondRingLinesCount: 10,
                    secondRingAngle: 36,
                    thirdRingRadius: 14.5,
                    thirdRingLinesCount: 16,
                    thirdRingAngle: 22.5,
                }, {
                    siloDiameter: 35.5,
                    firstRingRadius: 2.8,
                    firstRingLinesCount: 3,
                    firstRingAngle: 120,
                    secondRingRadius: 8.5,
                    secondRingLinesCount: 10,
                    secondRingAngle: 36,
                    thirdRingRadius: 14.5,
                    thirdRingLinesCount: 16,
                    thirdRingAngle: 22.5,
                }, {
                    siloDiameter: 36,
                    firstRingRadius: 2.8,
                    firstRingLinesCount: 3,
                    firstRingAngle: 120,
                    secondRingRadius: 8.6,
                    secondRingLinesCount: 10,
                    secondRingAngle: 36,
                    thirdRingRadius: 14.5,
                    thirdRingLinesCount: 16,
                    thirdRingAngle: 22.5,
                }, {
                    siloDiameter: 36.5,
                    firstRingRadius: 2.8,
                    firstRingLinesCount: 3,
                    firstRingAngle: 120,
                    secondRingRadius: 8.6,
                    secondRingLinesCount: 10,
                    secondRingAngle: 36,
                    thirdRingRadius: 14.8,
                    thirdRingLinesCount: 16,
                    thirdRingAngle: 22.5,
                }, {
                    siloDiameter: 37,
                    firstRingRadius: 2.8,
                    firstRingLinesCount: 3,
                    firstRingAngle: 120,
                    secondRingRadius: 8.6,
                    secondRingLinesCount: 10,
                    secondRingAngle: 36,
                    thirdRingRadius: 14.8,
                    thirdRingLinesCount: 16,
                    thirdRingAngle: 22.5,
                }, {
                    siloDiameter: 37.5,
                    zeroRingLinesCount: 1,
                    firstRingRadius: 5.3,
                    firstRingLinesCount: 4,
                    firstRingAngle: 90,
                    secondRingRadius: 10,
                    secondRingLinesCount: 10,
                    secondRingAngle: 36,
                    thirdRingRadius: 15,
                    thirdRingLinesCount: 17,
                    thirdRingAngle: 21.2,
                }, {
                    siloDiameter: 38,
                    zeroRingLinesCount: 1,
                    firstRingRadius: 5.3,
                    firstRingLinesCount: 4,
                    firstRingAngle: 90,
                    secondRingRadius: 10,
                    secondRingLinesCount: 10,
                    secondRingAngle: 36,
                    thirdRingRadius: 15,
                    thirdRingLinesCount: 17,
                    thirdRingAngle: 21.2,
                }, {
                    siloDiameter: 38.5,
                    zeroRingLinesCount: 1,
                    firstRingRadius: 5.3,
                    firstRingLinesCount: 4,
                    firstRingAngle: 90,
                    secondRingRadius: 10,
                    secondRingLinesCount: 10,
                    secondRingAngle: 36,
                    thirdRingRadius: 15,
                    thirdRingLinesCount: 17,
                    thirdRingAngle: 21.2,
                }, {
                    siloDiameter: 39,
                    zeroRingLinesCount: 1,
                    firstRingRadius: 5.3,
                    firstRingLinesCount: 5,
                    firstRingAngle: 72,
                    secondRingRadius: 10.5,
                    secondRingLinesCount: 10,
                    secondRingAngle: 36,
                    thirdRingRadius: 17,
                    thirdRingLinesCount: 18,
                    thirdRingAngle: 20,
                }, {
                    siloDiameter: 39.5,
                    zeroRingLinesCount: 1,
                    firstRingRadius: 5.3,
                    firstRingLinesCount: 5,
                    firstRingAngle: 72,
                    secondRingRadius: 11,
                    secondRingLinesCount: 10,
                    secondRingAngle: 36,
                    thirdRingRadius: 17,
                    thirdRingLinesCount: 18,
                    thirdRingAngle: 20,
                }, {
                    siloDiameter: 40,
                    zeroRingLinesCount: 1,
                    firstRingRadius: 5.6,
                    firstRingLinesCount: 5,
                    firstRingAngle: 72,
                    secondRingRadius: 11.3,
                    secondRingLinesCount: 10,
                    secondRingAngle: 36,
                    thirdRingRadius: 17,
                    thirdRingLinesCount: 18,
                    thirdRingAngle: 20,
                }
            ]
        }
    ).name;
